import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Grid,
  Chip,
  IconButton
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { campaignTypes, discountTypes, scanTriggers, mockProducts, mockRegions } from '../data/mockCampaigns';
import { mockSegments } from '../data/mockSegments';

const CampaignCreate = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    message: '',
    startDate: '',
    endDate: '',
    budget: '',
    targetProducts: [],
    targetSegments: [],
    discountType: 'none',
    discountValue: '',
    scanTrigger: 'first_scan',
    redemptionLimit: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Campaign name is required';
    if (!formData.type) newErrors.type = 'Campaign type is required';
    if (formData.targetProducts.length === 0) newErrors.targetProducts = 'At least one product must be selected';
    if (formData.targetSegments.length === 0) newErrors.targetSegments = 'At least one customer segment must be selected';
    if (!formData.message.trim()) newErrors.message = 'Campaign message is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (!formData.budget || formData.budget <= 0) newErrors.budget = 'Valid budget is required';
    if (formData.discountType !== 'none' && (!formData.discountValue || formData.discountValue <= 0)) {
      newErrors.discountValue = 'Discount value is required when discount type is selected';
    }

    if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (validateForm()) {
      console.log('Campaign created:', formData);
      // Here we would typically save to state or API
      alert('Campaign created successfully!');

      // Navigate back to campaign list
      navigate('/marketing');
    }
  };

  const handleCancel = () => {
    navigate('/marketing');
  };

  const handleMultiSelectChange = (field) => (event) => {
    const value = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    setFormData({
      ...formData,
      [field]: value
    });

    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const getTypeDescription = (type) => {
    const typeConfig = campaignTypes.find(t => t.value === type);
    return typeConfig ? typeConfig.description : '';
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate('/marketing')} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight={600}>
              Create New Campaign
            </Typography>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <CardContent>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={formData.name}
                onChange={handleChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Campaign Type"
                value={formData.type}
                onChange={handleChange('type')}
                error={!!errors.type}
                helperText={errors.type}
                required
              >
                {campaignTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        size="small"
                        label={type.label}
                        sx={{
                          backgroundColor: `${type.color}20`,
                          color: type.color,
                          fontWeight: 500
                        }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {formData.type && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {getTypeDescription(formData.type)}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Target Products"
                value={formData.targetProducts}
                onChange={handleMultiSelectChange('targetProducts')}
                error={!!errors.targetProducts}
                helperText={errors.targetProducts || 'Select wines for this campaign'}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => selected.join(', ')
                }}
                required
              >
                {mockProducts.map((product) => (
                  <MenuItem key={product} value={product}>
                    {product}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Target Customer Segments"
                value={formData.targetSegments}
                onChange={handleMultiSelectChange('targetSegments')}
                error={!!errors.targetSegments}
                helperText={errors.targetSegments || 'Select customer segments to target'}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((segmentId) => {
                        const segment = mockSegments.find(s => s.id === segmentId);
                        return segment ? (
                          <Chip
                            key={segmentId}
                            label={`${segment.name} (${segment.estimatedSize})`}
                            size="small"
                            sx={{ backgroundColor: `${segment.color}20`, color: segment.color }}
                          />
                        ) : null;
                      })}
                    </Box>
                  )
                }}
                required
              >
                {mockSegments.map((segment) => (
                  <MenuItem key={segment.id} value={segment.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {segment.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {segment.description} • {segment.estimatedSize} customers
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Campaign Message"
                value={formData.message}
                onChange={handleChange('message')}
                error={!!errors.message}
                helperText={errors.message || 'The main message customers will see'}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={formData.startDate}
                onChange={handleChange('startDate')}
                error={!!errors.startDate}
                helperText={errors.startDate}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={formData.endDate}
                onChange={handleChange('endDate')}
                error={!!errors.endDate}
                helperText={errors.endDate}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Discount Type"
                value={formData.discountType}
                onChange={handleChange('discountType')}
                helperText="Reward customers get when scanning"
              >
                {discountTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {formData.discountType !== 'none' && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={formData.discountType === 'percentage' ? 'Discount %' : 'Discount Amount ($)'}
                  value={formData.discountValue}
                  onChange={handleChange('discountValue')}
                  error={!!errors.discountValue}
                  helperText={errors.discountValue}
                  inputProps={{ min: 0, max: formData.discountType === 'percentage' ? 100 : undefined }}
                />
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Scan Trigger"
                value={formData.scanTrigger}
                onChange={handleChange('scanTrigger')}
                helperText="When to trigger the campaign"
              >
                {scanTriggers.map((trigger) => (
                  <MenuItem key={trigger.value} value={trigger.value}>
                    {trigger.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Redemption Limit"
                value={formData.redemptionLimit}
                onChange={handleChange('redemptionLimit')}
                helperText="Maximum number of redemptions (0 = unlimited)"
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Budget ($)"
                value={formData.budget}
                onChange={handleChange('budget')}
                error={!!errors.budget}
                helperText={errors.budget}
                inputProps={{ min: 0, step: 100 }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  sx={{ textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(23, 49, 49, 0.3)'
                    }
                  }}
                >
                  Create Campaign
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CampaignCreate;